import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { toastNotification } from '../toast/Toast';

type DropzoneProps = {
    children: ReactNode | ReactNode[];
    onDrop?: (files: File[]) => void;
    onPaste?: (files: File[]) => void;
    disabled?: boolean;
    fileLimit?: number;
    className?: string;
};

const activeClasses = ['outline-dashed', 'outline-indigo-500'];

function Dropzone({ children, onDrop, onPaste, disabled = false, fileLimit = 1, className }: DropzoneProps): JSX.Element {
    const ref = useRef<HTMLDivElement | null>(null);
    // Ref for a draggable element that will unfortuantely trigger the dropzone
    const dragRef = useRef<HTMLElement | null>(null);
    function handleEnter(event: DragEvent): void {
        if (dragRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        ref.current?.classList.add(...activeClasses);
        ref.current?.classList.remove('border-transparent');
    }

    function handleLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        // if (event.target && ref.current?.contains(event.target as Node)) {
        //     return;
        // }

        ref.current?.classList.remove(...activeClasses);
        ref.current?.classList.add('border-transparent');
    }

    function handleDrag(event: DragEvent): void {
        if (event.type === 'drag') {
            if ((event.target as HTMLElement).draggable) {
                dragRef.current = event.target as HTMLElement;
            }
        }

        if (dragRef.current) {
            return;
        } else {
            event.preventDefault();
            event.stopPropagation();
        }

        ref.current?.classList.add(...activeClasses);
        ref.current?.classList.remove('border-transparent');
    }

    function handleDragEnd(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (dragRef.current) {
            dragRef.current = null;
        }
        ref.current?.classList.remove(...activeClasses);
        ref.current?.classList.add('border-transparent');
    }

    const handlePaste = useCallback(
        function (event: ClipboardEvent): void {
            if (event.clipboardData?.getData('text')) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const filelist = event.clipboardData?.files;
            if (filelist) {
                if (filelist.length > fileLimit) {
                    toastNotification('error', `You can't upload more than ${fileLimit} ${fileLimit > 1 ? 'files' : 'file'}.`);
                    return;
                }
                onPaste && onPaste(Array.from(filelist));
            }
        },
        [onPaste, fileLimit]
    );

    const handleDrop = useCallback(
        function (event: DragEvent): void {
            if (dragRef.current) {
                dragRef.current = null;
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            ref.current?.classList.remove(...activeClasses);
            ref.current?.classList.add('border-transparent');
            const filelist = event.dataTransfer?.files;
            if (filelist) {
                if (filelist.length > fileLimit) {
                    toastNotification('error', `You can't upload more than ${fileLimit} ${fileLimit > 1 ? 'files' : 'file'}.`);
                    return;
                }
                onDrop && onDrop(Array.from(filelist));
            }
        },
        [onDrop, fileLimit]
    );

    useEffect(() => {
        if (ref?.current) {
            const _current = ref?.current;
            _current.addEventListener('drop', handleDrop);
            _current.addEventListener('dragenter', handleEnter);
            _current.addEventListener('dragleave', handleLeave);
            _current.addEventListener('dragover', handleEnter);
            _current.addEventListener('dragend', handleDragEnd);
            _current.addEventListener('drag', handleDrag);
            _current.addEventListener('paste', handlePaste);
            return (): void => {
                _current?.removeEventListener('dragenter', handleEnter);
                _current?.removeEventListener('dragleave', handleLeave);
                _current?.removeEventListener('drop', handleDrop);
                _current?.removeEventListener('dragover', handleDrag);
                _current?.removeEventListener('dragend', handleDragEnd);
                _current?.removeEventListener('drag', handleDrag);
                _current?.removeEventListener('paste', handlePaste);
            };
        }
    }, [handleDrop, handlePaste]);

    if (disabled) {
        return <>{children}</>;
    }
    return (
        <div ref={ref} className={`w-full grow h-full ${className}`}>
            {children}
        </div>
    );
}
export default Dropzone;
